import type { Lang, SiteCopy } from '../i18n/types';
import { fetchBlogList, type BlogListItem, type BlogPost } from './api';
import { renderMarkdown } from './markdown';
import { blogListPath, blogPostPath } from '../routes';
import { blogListSeoMeta, blogPostSeoMeta } from '../seo/keywords';

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function fmtDate(iso: string | null, isZh: boolean): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(isZh ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function coverSrc(url: string | null): string {
  if (!url) return '/images/hero-orbit.svg';
  if (url.startsWith('http') || url.startsWith('/')) return url;
  return `/${url}`;
}

function cardHtml(post: BlogListItem, lang: Lang, isZh: boolean): string {
  const href = blogPostPath(lang, post.id);
  const img = coverSrc(post.cover_image);
  const date = fmtDate(post.published_at ?? post.updated_at, isZh);
  return `
    <a class="blog-card reveal" href="${esc(href)}">
      <div class="blog-card__media">
        <img src="${esc(img)}" alt="" loading="lazy" width="640" height="360" />
      </div>
      <div class="blog-card__body">
        <time class="blog-card__date" datetime="${esc(post.published_at ?? post.updated_at)}">${esc(date)}</time>
        <h2 class="blog-card__title">${esc(post.title)}</h2>
      </div>
    </a>`;
}

function paginationHtml(
  page: number,
  totalPages: number,
  lang: Lang,
  isZh: boolean,
): string {
  if (totalPages <= 1) return '';
  const base = blogListPath(lang);
  const prev = page > 1 ? `${base}?page=${page - 1}` : '';
  const next = page < totalPages ? `${base}?page=${page + 1}` : '';
  return `
    <nav class="blog-pagination" aria-label="${isZh ? '博客分页' : 'Blog pagination'}">
      <a class="btn btn-ghost blog-pagination__btn${prev ? '' : ' is-disabled'}" href="${prev || '#'}" ${prev ? '' : 'aria-disabled="true"'}>${isZh ? '上一页' : 'Previous'}</a>
      <span class="blog-pagination__info">${page} / ${totalPages}</span>
      <a class="btn btn-ghost blog-pagination__btn${next ? '' : ' is-disabled'}" href="${next || '#'}" ${next ? '' : 'aria-disabled="true"'}>${isZh ? '下一页' : 'Next'}</a>
    </nav>`;
}

export async function renderBlogListSection(lang: Lang, copy: SiteCopy): Promise<string> {
  const isZh = lang === 'zh';
  const page = Math.max(1, Number(new URLSearchParams(location.search).get('page') ?? '1') || 1);
  const emptyMsg = isZh ? '暂无已发布文章' : 'No published posts yet.';

  let data: Awaited<ReturnType<typeof fetchBlogList>> | null = null;
  try {
    data = await fetchBlogList(lang, page, 9);
  } catch {
    data = null;
  }

  const cards =
    data && data.posts.length > 0
      ? data.posts.map((p) => cardHtml(p, lang, isZh)).join('')
      : `<p class="blog-empty">${emptyMsg}</p>`;

  return `
    <section class="section blog-section section--alt" id="top">
      <div class="section__inner reveal">
        <header class="section-header section-header--center">
          <p class="section__eyebrow">${esc(copy.blog.eyebrow)}</p>
          <h1 class="section__title">${esc(copy.blog.listTitle)}</h1>
          <p class="section__subtitle section__subtitle--center">${esc(copy.blog.listSubtitle)}</p>
        </header>
        <div class="blog-grid">${cards}</div>
        ${data ? paginationHtml(data.page, data.totalPages, lang, isZh) : ''}
      </div>
    </section>`;
}

export function renderBlogPostHtml(post: BlogPost, lang: Lang, copy: SiteCopy): string {
  const isZh = lang === 'zh';
  const back = blogListPath(lang);
  const html = renderMarkdown(post.content_md);
  const date = fmtDate(post.published_at ?? post.updated_at, isZh);
  const cover = post.cover_image
    ? `<img class="blog-article__cover" src="${esc(coverSrc(post.cover_image))}" alt="" />`
    : '';

  return `
    <article class="section blog-article" id="top">
      <div class="section__inner reveal">
        <a class="blog-back" href="${esc(back)}">← ${esc(copy.blog.backToList)}</a>
        ${cover}
        <time class="blog-article__date" datetime="${esc(post.published_at ?? post.updated_at)}">${esc(date)}</time>
        <h1 class="blog-article__title">${esc(post.title)}</h1>
        <div class="blog-prose">${html}</div>
      </div>
    </article>`;
}

export function renderBlogPostError(lang: Lang, copy: SiteCopy, notFound: boolean): string {
  const isZh = lang === 'zh';
  const back = blogListPath(lang);
  return `
    <section class="section blog-section" id="top">
      <div class="section__inner reveal">
        <a class="blog-back" href="${esc(back)}">← ${esc(copy.blog.backToList)}</a>
        <p class="blog-empty">${notFound ? (isZh ? '文章不存在' : 'Post not found') : isZh ? '文章暂不可用' : 'This post is unavailable.'}</p>
      </div>
    </section>`;
}

export function blogSeoMeta(
  lang: Lang,
  page: 'list' | 'post',
  post?: { title: string; excerpt?: string | null; meta_description?: string | null; keywords?: string | null },
) {
  if (page === 'post' && post) return blogPostSeoMeta(lang, post);
  return blogListSeoMeta(lang);
}
